import React from 'react';
import {
  Home,
  Search,
  PlusCircle,
  MessageSquare,
  User,
  Bell,
  Briefcase,
  ShieldCheck,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const MobileBottomNav: React.FC = () => {
  const { currentView, navigateTo, currentUser, unreadNotifsCount, messages, openAuthModal } = useApp();

  // Count unread or active messages
  const hasUnreadMessages = messages.length > 0;

  // Determine dashboard view based on role
  const handleAccountClick = () => {
    if (!currentUser) {
      openAuthModal('login');
      return;
    }
    if (currentUser.role === 'professional') {
      navigateTo('pro_dashboard');
    } else if (currentUser.role === 'admin') {
      navigateTo('admin_dashboard');
    } else {
      navigateTo('customer_dashboard');
    }
  };

  const isHomeActive = currentView === 'home';
  const isSearchActive = currentView === 'find_pro' || currentView === 'available_jobs';
  const isPostJobActive = currentView === 'post_job';
  const isMessagesActive = currentView === 'messages';
  const isDashboardActive =
    currentView === 'customer_dashboard' ||
    currentView === 'pro_dashboard' ||
    currentView === 'admin_dashboard' ||
    currentView === 'pro_profile';

  const searchTarget = currentUser?.role === 'professional' ? 'available_jobs' : 'find_pro';
  const searchLabel = currentUser?.role === 'professional' ? 'Find Jobs' : 'Find Pros';

  return (
    <nav
      id="mobile-bottom-navigation"
      aria-label="Mobile Navigation"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 px-1.5 py-1.5 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.4)] pb-[max(0.5rem,env(safe-area-inset-bottom))] transition-colors duration-200"
    >
      <div className="grid grid-cols-5 items-center max-w-lg mx-auto">
        {/* 1. Home */}
        <button
          id="mobile-nav-home-btn"
          onClick={() => navigateTo('home')}
          className={`flex flex-col items-center justify-center py-1 min-h-[48px] rounded-xl transition-all cursor-pointer min-w-0 ${
            isHomeActive
              ? 'text-emerald-600 dark:text-emerald-400 font-bold'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <Home className={`w-5 h-5 shrink-0 ${isHomeActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
          <span className="text-[10px] tracking-tight mt-0.5 truncate max-w-full px-0.5">Home</span>
        </button>

        {/* 2. Browse / Search */}
        <button
          id="mobile-nav-search-btn"
          onClick={() => navigateTo(searchTarget)}
          className={`flex flex-col items-center justify-center py-1 min-h-[48px] rounded-xl transition-all cursor-pointer min-w-0 ${
            isSearchActive
              ? 'text-emerald-600 dark:text-emerald-400 font-bold'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <Search className={`w-5 h-5 shrink-0 ${isSearchActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
          <span className="text-[10px] tracking-tight mt-0.5 truncate max-w-full px-0.5">{searchLabel}</span>
        </button>

        {/* 3. Primary Post Job Action (Elevated Center Button) */}
        <button
          id="mobile-nav-post-job-btn"
          onClick={() => navigateTo('post_job')}
          aria-label="Post a Job"
          className="flex flex-col items-center justify-center -mt-4 min-h-[48px] group cursor-pointer min-w-0"
        >
          <div
            className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white shadow-lg transition-transform active:scale-95 shrink-0 ${
              isPostJobActive
                ? 'bg-emerald-700 ring-4 ring-emerald-100 dark:ring-emerald-950 shadow-emerald-600/30'
                : 'bg-emerald-600 hover:bg-emerald-700 ring-4 ring-white dark:ring-slate-900 shadow-emerald-600/30'
            }`}
          >
            <PlusCircle className="w-5 h-5 sm:w-6 sm:h-6 stroke-[2.5px]" />
          </div>
          <span className="text-[10px] font-bold text-slate-800 dark:text-slate-200 mt-0.5 truncate max-w-full px-0.5">
            Post Job
          </span>
        </button>

        {/* 4. Messages */}
        <button
          id="mobile-nav-messages-btn"
          onClick={() => navigateTo('messages')}
          className={`flex flex-col items-center justify-center py-1 min-h-[48px] rounded-xl relative transition-all cursor-pointer min-w-0 ${
            isMessagesActive
              ? 'text-emerald-600 dark:text-emerald-400 font-bold'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <div className="relative">
            <MessageSquare className={`w-5 h-5 shrink-0 ${isMessagesActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
            {hasUnreadMessages && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-white dark:ring-slate-900" />
            )}
          </div>
          <span className="text-[10px] tracking-tight mt-0.5 truncate max-w-full px-0.5">Messages</span>
        </button>

        {/* 5. Dashboard / Account */}
        <button
          id="mobile-nav-dashboard-btn"
          onClick={handleAccountClick}
          className={`flex flex-col items-center justify-center py-1 min-h-[48px] rounded-xl relative transition-all cursor-pointer min-w-0 ${
            isDashboardActive
              ? 'text-emerald-600 dark:text-emerald-400 font-bold'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <div className="relative">
            {currentUser?.role === 'professional' ? (
              <Briefcase className={`w-5 h-5 shrink-0 ${isDashboardActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
            ) : currentUser?.role === 'admin' ? (
              <ShieldCheck className={`w-5 h-5 shrink-0 ${isDashboardActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
            ) : (
              <User className={`w-5 h-5 shrink-0 ${isDashboardActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
            )}
            {unreadNotifsCount > 0 && (
              <span className="absolute -top-1 -right-1.5 w-4 h-4 bg-rose-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center ring-1.5 ring-white dark:ring-slate-900">
                {unreadNotifsCount > 9 ? '9+' : unreadNotifsCount}
              </span>
            )}
          </div>
          <span className="text-[10px] tracking-tight mt-0.5 truncate max-w-full px-0.5">
            {!currentUser
              ? 'Account'
              : currentUser.role === 'professional'
              ? 'Dashboard'
              : currentUser.role === 'admin'
              ? 'Admin'
              : 'My Jobs'}
          </span>
        </button>
      </div>
    </nav>
  );
};
